import HexagonLoader from '../../../components/HexagonLoader/HexagonLoader';
import { useState, useEffect, useCallback } from 'react';
import TenancyStatsTable from '../ExecutiveDash/TenancyStatsTable';
import AnchorTradingData from '../ExecutiveDash/AnchorTradingData';
import { useAuth } from '../../../context/AuthContext';
import UserAvatarSVG from '../../../components/UserAvatarSVG';
import '../../../styles/LeasingDashStyles.scss';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList, Cell, LineChart, Line } from 'recharts';
import * as Sentry from '@sentry/react';

const UnsignedLeasesTooltip = ({ active, payload, label, isHovered, lockedData, onLock, onUnlock }) => {
    const displayData = isHovered
        ? lockedData
        : (active && payload?.length ? { payload, label } : null);

    if (!displayData?.payload?.length) return null;

    const tenants = displayData.payload[0]?.payload?.tenants ?? [];
    const displayLabel = displayData.label;

    return (
        <div
            onMouseEnter={() => onLock?.({ payload, label })}
            onMouseLeave={onUnlock}
            onMouseMove={e => e.stopPropagation()}
            style={{
                maxWidth: 500,
                border: '1px solid #ccc',
                borderRadius: 4,
                background: 'white',
                padding: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                fontSize: 13,
            }}
        >
            <strong style={{ display: 'block', marginBottom: 6 }}>
                {displayLabel} — {tenants.length} unsigned
            </strong>
            {tenants.length === 0 ? (
                <span style={{ color: '#888' }}>None</span>
            ) : (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                <th style={{ width: '50%', padding: '8px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid #ddd' }}>
                                    Property
                                </th>
                                <th style={{ width: '50%', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                                    Tenant
                                </th>
                            </tr>
                        </thead>
                    </table>
                    <div style={{ maxHeight: 300, overflowY: 'auto', borderBottom: '1px solid #ccc' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody>
                                {tenants.map((t, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ width: '50%', padding: '6px 8px', borderRight: '1px solid #eee' }}>
                                            {t.propertyName || '—'}
                                        </td>
                                        <td style={{ width: '50%', padding: '6px 8px' }}>
                                            {t.tenantName || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const ExpiredLeasesTooltip = ({ active, payload, label, isHovered, lockedData, onLock, onUnlock }) => {
    const displayData = isHovered
        ? lockedData
        : (active && payload?.length ? { payload, label } : null);

    if (!displayData?.payload?.length) return null;

    const leases = displayData.payload[0]?.payload?.leases ?? [];
    const displayLabel = displayData.label;

    return (
        <div
            onMouseEnter={() => onLock?.({ payload, label })}
            onMouseLeave={onUnlock}
            onMouseMove={e => e.stopPropagation()}
            style={{
                maxWidth: 500,
                border: '1px solid #ccc',
                borderRadius: 4,
                background: 'white',
                padding: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                fontSize: 13,
            }}
        >
            <strong style={{ display: 'block', marginBottom: 6 }}>
                {displayLabel} — {leases.length} expired
            </strong>
            {leases.length === 0 ? (
                <span style={{ color: '#888' }}>None</span>
            ) : (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                <th style={{ width: '50%', padding: '8px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid #ddd' }}>
                                    Property
                                </th>
                                <th style={{ width: '50%', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>
                                    Tenant
                                </th>
                            </tr>
                        </thead>
                    </table>
                    <div style={{ maxHeight: 300, overflowY: 'auto', borderBottom: '1px solid #ccc' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody>
                                {leases.map((l, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ width: '50%', padding: '6px 8px', borderRight: '1px solid #eee' }}>
                                            {l.propertyName || '—'}
                                        </td>
                                        <td style={{ width: '50%', padding: '6px 8px' }}>
                                            {l.tenantName || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const LeasingDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [initialLoading, setInitialLoading] = useState(true);
    const [period, setPeriod] = useState(null);
    const [totalArea, setTotalArea] = useState(0);
    const [condensedIncomeData, setCondensedIncomeData] = useState(null);
    const [expiredButActiveLeases, setExpiredButActiveLeases] = useState();
    const [unsignedLeasesData, setUnsignedLeasesData] = useState({});
    const [vacanciesData, setVacanciesData] = useState([]);
    const [PropertyShortCodes, setPropertyShortCodes] = useState({});
    const [isUnsignedTooltipHovered, setIsUnsignedTooltipHovered] = useState(false);
    const [lockedTooltipData, setLockedTooltipData] = useState(null);

    const handleTooltipLock = useCallback((data) => {
        setLockedTooltipData(data);
        setIsUnsignedTooltipHovered(true);
    }, []);

    const handleTooltipUnlock = useCallback(() => {
        setIsUnsignedTooltipHovered(false);
    }, []);

    const [isExpiredTooltipHovered, setIsExpiredTooltipHovered] = useState(false);
    const [lockedExpiredTooltipData, setLockedExpiredTooltipData] = useState(null);

    const handleExpiredTooltipLock = useCallback((data) => {
        setLockedExpiredTooltipData(data);
        setIsExpiredTooltipHovered(true);
    }, []);

    const handleExpiredTooltipUnlock = useCallback(() => {
        setIsExpiredTooltipHovered(false);
    }, []);

    useEffect(() => {
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`);
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                // Only log errors that aren't token refresh failures
                if (e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
    }, []);

    // Fetch property short codes via MDA external mappings
    useEffect(() => {
        const fetchPropertyShortCodes = async () => {
            try {
                const response = await apiFetch('/api/Property/external-mappings?externalSystem=MDA');
                const data = await response.json();

                if (data.success && data.data) {
                    // Build a mapping of MDA PropertyName -> ShortCode
                    const CodesMap = {};
                    data.data.forEach(mapping => {
                        const mdaName = (mapping.externalPropertyName || mapping.ExternalPropertyName)?.trim();
                        const shortCode = mapping.shortCode || mapping.ShortCode;
                        if (mdaName && shortCode) {
                            CodesMap[mdaName] = shortCode;
                        }
                    });
                    setPropertyShortCodes(CodesMap);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (e.message !== 'Token refresh failed') {
                    console.error('Error fetching property short codes:', e);
                }
            }
        };
        fetchPropertyShortCodes();
    }, [apiFetch]);

    // Function to fetch property data
    const fetchPropertyData = async () => {
        try {
            const response = await apiFetch(`/api/Mda/PropertyData?Period=${period}`);
            const data = await response.json();

            if (data.success) {
                const sum = Object.entries(data.data).reduce((acc, [propertyName, area]) => {
                    // Exclude "Unity Centre" and only add if the area is a valid number
                    if (propertyName === "Unity Centre") return acc;
                    const numericArea = Number(area);
                    return acc + (isNaN(numericArea) ? 0 : numericArea);
                }, 0);

                // Round up to the nearest full number, no comma
                setTotalArea(Math.ceil(sum));
            } 
        } catch (err) {
            Sentry.captureException(err);
            // Only log errors that aren't token refresh failures
            if (err.message !== 'Token refresh failed') {
                console.error('Error fetching property data:', err);
            }
        } 
    };

    const fetchCondensedIncomeStatement = async () => {
        try {
            const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`);
            const data = await response.json();

            if (data.success) {
                setCondensedIncomeData(data.data);
            } else {
                console.error('Failed to fetch trial balance data:', data.message);
            }
        } catch (err) {
            Sentry.captureException(err);
            // Only log errors that aren't token refresh failures
            if (err.message !== 'Token refresh failed') {
                console.error('Error fetching trial balance data:', err);
            }
        } 
    };

    const fetchExpiredButActiveLeaseData = async () => {
        try {
            const response = await apiFetch(`/api/Mda/ExpiredButActiveLeases?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setExpiredButActiveLeases(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching expired but active lease counts:', e);
        }
    };

    const fetchUnsignedLeasesData = async () => {
        try {
            const response = await apiFetch(`/api/Mda/UnsignedTenantsDetailByPeriod?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setUnsignedLeasesData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching unsigned leases data:', e);
        }
    };

    const fetchVacanciesData = async () => {
        try {
            const response = await apiFetch(`/api/Mda/VacancyByPeriod?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setVacanciesData(Array.isArray(data.data) ? data.data : []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching vacancies data:', e);
        }
    };

    useEffect(() => {
        if (!period) return;
        const timer = setTimeout(() => {
            fetchPropertyData();
            fetchCondensedIncomeStatement();
            fetchExpiredButActiveLeaseData();
            fetchUnsignedLeasesData();
            fetchVacanciesData();
        }, 0);
        return () => clearTimeout(timer);
    }, [period]);

    function getYtdBasicContractualVarianceByProperty(condensedIncomeData) {
        if (!Array.isArray(condensedIncomeData)) return [];

        const excludedProperties = [
            "Unity Centre", "Bridge City", "Central City", "Commprop Fund", "Eden Square",
            "Garankuwa", "Lenasia", "Madeira", "Mala", "Masignita", "Mega City", "Mkuzi",
            "PIC Fund", "Piet Retief", "Shoprite", "Temba City", "Tembisa"
        ];

        const PropertyMap = {};

        condensedIncomeData.forEach(Item => {
            if (
                Item.Caption === "Basic Contractual Income" &&
                !excludedProperties.includes(Item.PropertyName)
            ) {
                const Property = Item.PropertyName;
                if (!PropertyMap[Property]) {
                    PropertyMap[Property] = { actual: 0, budget: 0 };
                }
                const Actual = typeof Item.CurrentBal === 'number'
                    ? Item.CurrentBal
                    : parseFloat(Item.CurrentBal || '0');
                const Budget = typeof Item.BudgetYearToDate === 'number'
                    ? Item.BudgetYearToDate
                    : parseFloat(Item.BudgetYearToDate || '0');
                PropertyMap[Property].actual += Actual;
                PropertyMap[Property].budget += Budget;
            }
        });

        // Build property data array using shortcodes from PropertyShortCodes state
        const PropertyData = Object.entries(PropertyMap).map(([Property, { actual, budget }]) => {
            // Use the shortcode from the PropertyShortCodes mapping, or fallback to property name
            const DisplayName = PropertyShortCodes[Property] || Property;
            
            return {
                property: DisplayName,
                variance: (actual - budget) * -1
            };
        });

        // Calculate total actual and budget for displayed properties
        const TotalActual = Object.values(PropertyMap).reduce((sum, { actual }) => sum + actual, 0);
        const TotalBudget = Object.values(PropertyMap).reduce((sum, { budget }) => sum + budget, 0);
        const TotalVariance = (TotalActual - TotalBudget) * -1;

        // Add portfolio total as the last entry
        PropertyData.push({
            property: 'Portfolio',
            variance: TotalVariance
        });

        return PropertyData;
    }

    const data = getYtdBasicContractualVarianceByProperty(condensedIncomeData);

    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    function getLast12Periods(currentPeriod) {
        const periods = [];
        let year = Math.floor(currentPeriod / 100);
        let month = currentPeriod % 100;
        for (let i = 0; i < 12; i++) {
            periods.unshift(year * 100 + month);
            month--;
            if (month === 0) {
                year--;
                month = 12;
            }
        }
        return periods;
    }

    const unsignedLeasesChartData = (() => {
        if (!period || !unsignedLeasesData || Object.keys(unsignedLeasesData).length === 0) return [];
        const last12Periods = getLast12Periods(period);
        return last12Periods.map(p => {
            const year = String(p).slice(0, 4);
            const month = String(p).slice(4).padStart(2, '0');
            const date = new Date(Number(year), Number(month) - 1, 1);
            const label = date.toLocaleString('en-US', { month: 'short' }) + ' ' + year.slice(-2);
            return {
                period: label,
                value: unsignedLeasesData[p]?.total ?? 0,
                tenants: unsignedLeasesData[p]?.tenants ?? []
            };
        });
    })();

    const vacanciesChartData = (() => {
        if (!period || vacanciesData.length === 0) return [];
        const last12Periods = getLast12Periods(period);
        // Map period to value
        const periodToValue = Object.fromEntries(
            vacanciesData.map(row => [row.period, row.vacancies ?? 0])
        );
        // Build chart data
        return last12Periods.map(p => {
            const year = String(p).slice(0, 4);
            const month = String(p).slice(4).padStart(2, '0');
            const date = new Date(Number(year), Number(month) - 1, 1);
            const label = date.toLocaleString('en-US', { month: 'short' }) + ' ' + year.slice(-2);
            return {
                period: label,
                vacancies: periodToValue[p] ?? 0
            };
        });
    })();

    // Transform expiredButActiveLeases to chart data with formatted period labels
    const expiredLeasesChartData = expiredButActiveLeases
        ? Object.entries(expiredButActiveLeases).map(([p, periodData]) => {
            const year = p.substring(0, 4);
            const month = p.substring(4, 6);
            const date = new Date(Number(year), Number(month) - 1, 1);
            const label = date.toLocaleString('en-US', { month: 'short' }) + ' ' + String(date.getFullYear()).slice(-2);
            return {
                period: label,
                count: periodData?.total ?? 0,
                leases: periodData?.leases ?? []
            };
        })
        : [];


    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div className="leasing-dashboard-container">
                    <div className="leasing-dashboard-content">
                        <div className="leasing-dashboard-header">
                            <h1 className="dashboard-greeting">
                                <UserAvatarSVG />
                                Hello {user.FirstName || user.firstName}
                            </h1>
                            <h3>Leasing Dashboard</h3>
                        </div>
                        <div className="leasing-dashboard-top-row">
                            <div className="leasing-dashboard-col leasing-dashboard-col-left">
                                <div className="leasing-dashboard-section">
                                    <TenancyStatsTable period={period} totalArea={totalArea} />
                                </div>
                                <div className="leasing-dashboard-section">
                                    <AnchorTradingData period={period} />
                                </div>
                                    <div className="leasing-dashboard-section basic-graph">
                                        <div className="graph-heading">Expired But Active Leases (Last 12 Months)</div>
                                        <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
                                            <BarChart
                                                data={expiredLeasesChartData}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                                            >
                                                <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4"/>
                                                <XAxis dataKey="period" textAnchor="end" interval={0} height={60} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip
                                                    content={
                                                        <ExpiredLeasesTooltip
                                                            isHovered={isExpiredTooltipHovered}
                                                            lockedData={lockedExpiredTooltipData}
                                                            onLock={handleExpiredTooltipLock}
                                                            onUnlock={handleExpiredTooltipUnlock}
                                                        />
                                                    }
                                                    wrapperStyle={{
                                                        pointerEvents: 'auto',
                                                        overflow: 'visible',
                                                        zIndex: 9999,
                                                        ...(isExpiredTooltipHovered && { visibility: 'visible' })
                                                    }}
                                                />
                                                <Bar dataKey="count" fill="#f63040">
                                                    <LabelList dataKey="count" position="top" />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                            </div>
                            <div className="leasing-dashboard-col leasing-dashboard-col-right">
                                    <div className="leasing-dashboard-section basic-graph">
                                        <div className="graph-heading">Vacancies (Last 12 Months)</div>
                                        <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
                                            <LineChart
                                                data={vacanciesChartData}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                                            >
                                                <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                                                <XAxis dataKey="period" textAnchor="end" interval={0} height={60} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Line
                                                    type="monotone"
                                                    dataKey="vacancies"
                                                    stroke="#f63040"
                                                    strokeWidth={3}
                                                    dot={{ r: 5 }}
                                                    activeDot={{ r: 7 }}
                                                >
                                                    <LabelList dataKey="vacancies" position="top" />
                                                </Line>
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="leasing-dashboard-section basic-graph">
                                        <div className="graph-heading">Unsigned Leases (Last 12 Months)</div>
                                        <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
                                            <BarChart
                                                data={unsignedLeasesChartData}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                                            >
                                                <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                                                <XAxis dataKey="period" textAnchor="end" interval={0} height={60} />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip
                                                    content={
                                                        <UnsignedLeasesTooltip
                                                            isHovered={isUnsignedTooltipHovered}
                                                            lockedData={lockedTooltipData}
                                                            onLock={handleTooltipLock}
                                                            onUnlock={handleTooltipUnlock}
                                                        />
                                                    }
                                                    wrapperStyle={{
                                                        pointerEvents: 'auto',
                                                        overflow: 'visible',
                                                        zIndex: 9999,
                                                        ...(isUnsignedTooltipHovered && { visibility: 'visible' })
                                                    }}
                                                />
                                                <Bar dataKey="value" fill="#f63040">
                                                    <LabelList dataKey="value" position="top" />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                            </div>
                        </div>
                            <div className="leasing-dashboard-fullwidth-section">
                                <div className="basic-graph">
                                    <div className="graph-heading">
                                        YTD Basic Contractual Income Variance to Budget
                                    </div>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart
                                            data={data}
                                            margin={{ top: 20, right: 30, left: 50, bottom: 5 }}
                                        >
                                            <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                                            <XAxis dataKey="property" textAnchor="end" interval={0} height={80} />
                                            <YAxis
                                                label={{
                                                    value: 'Thousands',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                    dx: -20
                                                }}
                                                tickFormatter={value =>
                                                    value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                                                }
                                            />
                                            <Tooltip
                                                formatter={value =>
                                                    typeof value === 'number'
                                                        ? value.toLocaleString('en-US', {
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 2
                                                        }).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                                                        : value
                                                }
                                            />
                                            <Bar dataKey="variance" 
                                            >
                                                {data.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={entry.variance > 0 ? '#f63040' : '#222'}
                                                    />
                                                ))}
                                                <LabelList
                                                    dataKey="variance"
                                                    content={({ x, y, value, height }) => {
                                                        if (value == null) return null;
                                                        // Show value in thousands, integer only
                                                        const thousands = Math.trunc(Math.abs(value) / 1000);
                                                        const displayValue = value < 0 ? `-${thousands}` : thousands;

                                                        const verticalOffset = 8;
                                                        const labelY = value >= 0
                                                            ? y - verticalOffset
                                                            : y + height - verticalOffset;

                                                        return (
                                                            <text
                                                                x={x}
                                                                y={labelY}
                                                                fontSize={12}
                                                                fill="#222"
                                                                textAnchor="right"
                                                                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                                            >
                                                                {displayValue}
                                                                <title>{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</title>
                                                            </text>
                                                        );
                                                    }}
                                                />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default LeasingDashboard;